import { useState, useEffect } from "react";
import SimpleTable from "../../Component/Common/SimpleTable";
import BlueButton from "../../Component/Common/BlueButton";
import RegularLoader from "../../Component/Loader/RegularLoader";
import { Eye, Pencil, Trash2 } from "lucide-react";
import BadgeForm from "./BadgeForm";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { BASE_URL } from "../../config/api";
import RefreshButton from "../../Component/Common/RefreshButton";

export default function BadgeTable() {


  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const pageSize = 7;

  const noData = !loading && badges.length === 0;

  /* ---------------- FETCH ---------------- */
  const fetchBadges = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}badges`);
      const data = await res.json();
      if (!res.ok) throw new Error("Failed to fetch badges");
      setBadges(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges();
  }, []);

  const totalPages = Math.ceil(badges.length / pageSize);
  const paginatedBadges = badges.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  /* ---------------- HELPERS ---------------- */
  const getMethodBadge = (badge) => {
    if (badge.pay_now === 1) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
          Pay Now
        </span>
      );
    }
    if (badge.pay_later === 1) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
          Pay Later
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-500">
        —
      </span>
    );
  };

  /* ---------------- ACTIONS ---------------- */
  const openEditModal = (badge) => {
    setSelectedBadge(badge);
    setModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    setModalOpen(false);
    fetchBadges();
  };

  const handleDelete = async (badge) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete badge "${badge.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${BASE_URL}badges/${badge.id}`, {
          method: "DELETE",
        });

        if (!res.ok) throw new Error("Failed to delete badge");

        toast.success("Badge deleted successfully!");
        fetchBadges();
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      }
    }
  };

  /* ---------------- TABLE DATA ---------------- */
  const headers = [
    "Image",
    "Name",
    "Eligibility",
    "Method",
    "Created At",
    "Description",
    "Benefits",
  ];

  const rows = loading
  ? []
  : paginatedBadges.length === 0
  ? [
      {
        raw: null,
        cells: [
          <span
            className="block w-full text-center text-slate-400 py-6"
            colSpan={headers.length}
          >
            No badges added. Add a badge to view it here.
          </span>,
        ],
        colSpan: headers.length,
      },
    ]
  : paginatedBadges.map((badge) => ({
      raw: badge,
      cells: [
        <img
          src={badge.image_url}
          alt={badge.name}
          className="w-8 h-8 rounded-full object-cover border mx-auto"
        />,
        badge.name,
        badge.eligibility,
        getMethodBadge(badge),
        new Date(badge.created_at).toLocaleDateString(),
        badge.description,
        badge.benefits,
      ],
    }));


  /* ---------------- UI ---------------- */
  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-2">
      {/* Actions */}
      <div className="flex justify-end items-center mb-5 gap-2">
        {/* LEFT SIDE - Mobile Add Button */}
        <div className="md:hidden">
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-1 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow hover:bg-blue-700"
          >
            +
          </button>
        </div>

        {/* RIGHT SIDE - Refresh */}
        <RefreshButton onClick={fetchBadges} />
      </div>

      {/* Table */}
      {/* ================= DESKTOP TABLE ================= */}
<div className="hidden md:block">
  <SimpleTable
    headers={headers}
    rows={rows}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={setCurrentPage}
    pageSize={pageSize}
    renderActions={(badge) =>
      badge ? (
        <div className="flex gap-2">
          <button className="text-blue-600 hover:text-blue-800">
            <Eye size={16} />
          </button>
          <button
            className="text-amber-600 hover:text-amber-800"
            onClick={() => openEditModal(badge)}
          >
            <Pencil size={16} />
          </button>
          <button
            className="text-red-600 hover:text-red-800"
            onClick={() => handleDelete(badge)}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ) : null
    }
  />
</div>

      {/* ================= MOBILE CARDS ================= */}
      <div className="md:hidden space-y-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <RegularLoader />
          </div>
        ) : badges.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-8">
            No badges added yet.
          </div>
        ) : (
          paginatedBadges.map((badge) => {
            const method =
              badge.pay_now === 1
                ? { label: "Pay Now", color: "bg-green-500" }
                : badge.pay_later === 1
                ? { label: "Pay Later", color: "bg-amber-500" }
                : { label: "Basic", color: "bg-slate-400" };

            return (
              <div
                key={badge.id}
                className="relative bg-white rounded-3xl shadow-lg border border-slate-100 p-5"
              >
                {/* Floating Status Badge */}
                <div
                  className={`absolute -top-3 right-5 px-4 py-1 text-[11px] font-semibold text-white rounded-full shadow-md ${method.color}`}
                >
                  {method.label}
                </div>

                {/* Header */}
                <div className="flex gap-4 items-start">
                  {/* Badge Image */}
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center shadow-inner">
                    <img
                      src={badge.image_url}
                      alt={badge.name}
                      className="w-12 h-12 object-contain"
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-slate-900">
                      {badge.name}
                    </h3>

                    <p className="text-xs text-slate-500 mt-1">
                      {badge.eligibility}
                    </p>

                    <p className="mt-3 text-xs text-slate-600 leading-relaxed">
                      {badge.description}
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="my-4 border-t border-slate-100" />

                {/* Footer */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">
                    {new Date(badge.created_at).toLocaleDateString()}
                  </span>

                  <div className="flex gap-3">
                    <button className="w-9 h-9 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                      <Eye size={16} />
                    </button>

                    <button
                      onClick={() => openEditModal(badge)}
                      className="w-9 h-9 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center"
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      onClick={() => handleDelete(badge)}
                      className="w-9 h-9 rounded-xl bg-red-100 text-red-600 flex items-center justify-center"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>

            );
          })
        )}
      </div>



      {/* Loader Overlay */}
      {/* {loading && (
        <div className="py-8 flex justify-center">
          <RegularLoader />
        </div>
      )} */}

      {/* Edit Modal */}
      {modalOpen && selectedBadge && (
        <div className="block fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <BadgeForm
            badge={selectedBadge}
            onSuccess={handleUpdateSuccess}
            onClose={() => setModalOpen(false)}
          />
        </div>
      )}

      {/* Create Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <BadgeForm
            onSuccess={() => {
              setCreateOpen(false);
              fetchBadges();
            }}
            onClose={() => setCreateOpen(false)}
          />
        </div>
      )}

    </div>
  );
}





// import { useState, useEffect, useMemo } from "react";

// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import BlueButton from "../../Component/Common/BlueButton";
// import RegularLoader from "../../Component/Loader/RegularLoader";
// import { Eye, Pencil, Trash2 } from "lucide-react";
// // import EditLeadModal from "./EditLeadModal";
// // import { ViewLeadModal } from "./ViewLeadModal";

// export default function BadgeTable() {
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");
//   const [filterStatus, setFilterStatus] = useState("All");

//   const [viewModalOpen, setViewModalOpen] = useState(false);
//   const [selectedLeadForView, setSelectedLeadForView] = useState(null);

//   const [selectedLead, setSelectedLead] = useState(null);
//   const [editModalOpen, setEditModalOpen] = useState(false);



//   const pageSize = 7;

//   // Fetch leads from API
//   const fetchLeads = async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const res = await fetch(
//         "${BASE_URL}/warranty/wlead/list"
//       );
//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         throw new Error(data.message || "Failed to fetch leads");
//       }

//       // Map API response to expected fields
//       const mappedLeads = data.data.map((lead) => ({
//         id: lead.id,
//         name: lead.name,
//         owner: lead.owner_name || "-",
//         location: lead.district || lead.state || "-",
//         type: lead.lead_type || "-",
//         status: lead.status,
//         value: lead.lead_amount || 0,
//         createdAt: lead.created_at,

//         full: lead, // 🔥 store full API response here
//       }));


//       setLeads(mappedLeads);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchLeads();
//   }, []);

//   // Extract unique locations (e.g. district/state)
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   // Filter logic
//   const filteredLeads = useMemo(() => {
//     return leads
//       .filter((lead) =>
//         filterStatus === "All" ? true : lead.status === filterStatus
//       )
//       .filter((lead) => {
//         if (!startDate || !endDate) return true;
//         const created = new Date(lead.createdAt);
//         return created >= startDate && created <= endDate;
//       })
//       .filter((lead) =>
//         selectedLocation ? lead.location === selectedLocation : true
//       );
//   }, [leads, filterStatus, startDate, endDate, selectedLocation]);

//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   const resetPage = () => setCurrentPage(1);

//   const resetFilters = () => {
//   setFilterStatus("All");
//   setSelectedLocation("");
//   setStartDate(null);
//   setEndDate(null);
//   setCurrentPage(1);
// };


//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
//       {/* === Filters Row === */}
//       <div className="flex items-center justify-end mb-4 gap-2 flex-wrap">
//         {/* Status Filter */}
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             resetPage();
//           }}
//           className="text-xs md:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>

//         {/* Location Filter */}
//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             resetPage();
//           }}
//         />

//         {/* Date Range */}
//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             resetPage();
//           }}
//         />

//         <BlueButton
//           onClick={resetFilters}
//           className="text-xs md:text-sm border border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition"
//         >
//           Reset Filters
//         </BlueButton>

//         <RefreshButton onClick={fetchLeads} />

        
//       </div>

//       {/* === Table === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-4">Lead</th>
//               <th className="px-2">Owner</th>
//               <th className="px-2">Location</th>
//               <th className="px-2">Type</th>
//               <th className="px-2">Status</th>
//               <th className="px-2">Value</th>
//               <th className="px-2">Date</th>
//               <th className="px-2">Action</th>  
//             </tr>
//           </thead>

//           <tbody>
//             {loading ? (
//               <tr>
//                 <td colSpan="6" className="py-6 text-center">
//                   <div className="flex justify-center">
//                     <RegularLoader />
//                   </div>
//                 </td>
//               </tr>
//             ) : paginatedLeads.length === 0 ? (
//               <tr>
//                 <td colSpan="6" className="py-6 text-center text-slate-400">
//                   No leads match your filters.
//                 </td>
//               </tr>
//             ) : (
//               paginatedLeads.map((lead) => {
//                 const d = new Date(lead.createdAt);
//                 const monthLabel = monthLabels[d.getMonth()] || "—";

//                 const statusColor =
//                   lead.status === "won"
//                     ? "bg-green-50 text-green-700 border-green-100"
//                     : lead.status === "lost"
//                     ? "bg-red-50 text-red-700 border-red-100"
//                     : lead.status === "in progress"
//                     ? "bg-amber-50 text-amber-700 border-amber-100"
//                     : "bg-blue-50 text-blue-700 border-blue-100";

//                 return (
//                   <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                     <td className="py-2 pl-4">{lead.name}</td>
//                     <td className="px-2">{lead.owner}</td>
//                     <td className="px-2">{lead.location}</td>
//                     <td className="px-2">{lead.type || "-"}</td>
//                     <td className="px-2">
//                       <span
//                         className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusColor}`}
//                       >
//                         {lead.status}
//                       </span>
//                     </td>
//                     <td className="px-2">{formatCurrency(lead.value)}</td>
//                     <td className="px-2">{monthLabel}</td>
//                     <td className="px-2 flex items-center gap-2">
//                       {/* View */}
//                       <button
//                         className="text-blue-600 hover:text-blue-800"
//                         onClick={() => {
//                           setSelectedLeadForView(lead.full);
//                           setViewModalOpen(true);
//                         }}
//                       >
//                         <Eye size={16} />
//                       </button>


//                       {/* If lost → show ❌ instead of edit button */}
//                       {lead.status === "lost" ? (
//                         <span className="text-slate-400 cursor-not-allowed">
//                           ❌
//                         </span>
//                       ) : (
//                         <button 
//                           className="text-amber-600 hover:text-amber-800 text-center"
//                           onClick={() => {
//                               setSelectedLead(lead.full); // 🔥 pass original lead object
//                               setEditModalOpen(true);
//                             }}
//                         >
//                           <Pencil size={16} />
//                         </button>
//                       )}

//                       {/* Delete */}
//                       {/* <button 
//                         className="text-red-600 hover:text-red-800"
//                         onClick={() => {
//                           if (window.confirm(`Delete lead: ${lead.name}?`)) {
//                             console.log("Delete triggered", lead.id);
//                           }
//                         }}
//                       >
//                         <Trash2 size={16} />
//                       </button> */}
//                     </td>



//                   </tr>
//                 );
//               })
//             )}
//           </tbody>


//         </table>
//       </div>

//       {/* === Pagination === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}

//       {/* {viewModalOpen && selectedLeadForView && (
//         <ViewLeadModal
//           lead={selectedLeadForView} 
//           onClose={() => setViewModalOpen(false)} 
//         />
//       )} */}


//       {/* {editModalOpen && (
//         <EditLeadModal
//           lead={selectedLead}
//           onClose={() => setEditModalOpen(false)}
//         />
//       )} */}
//     </div>
//   );
// }




// import { useState, useMemo } from "react";

// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import { monthLabels } from "../../Component/EmployeeComponents/monthLabels";
// import { STATUSES } from "../../Component/EmployeeComponents/STATUSES";
// import Pagination from "../../Component/Common/Pagination";
// import LocationFilter from "../../Component/Common/LocationFilter";
// import DateRangePicker from "../../Component/Common/DateRangePicker";
// import RefreshButton from "../../Component/Common/RefreshButton";


// export default function LeadsTable({ leads, filterStatus, setFilterStatus }) {
//   const [currentPage, setCurrentPage] = useState(1);
//   const [startDate, setStartDate] = useState(null);
//   const [endDate, setEndDate] = useState(null);
//   const [selectedLocation, setSelectedLocation] = useState("");

//   const pageSize = 7;

//   // Extract unique locations (e.g. city)
//   const uniqueLocations = [...new Set(leads.map((l) => l.location))];

//   // ✅ Filter logic
//   const filteredLeads = useMemo(() => {
//   return leads
//     .filter((lead) =>
//       filterStatus === "All" ? true : lead.status === filterStatus
//     )
//     .filter((lead) => {
//       if (!startDate || !endDate) return true;
//       const created = new Date(lead.createdAt);
//       return created >= startDate && created <= endDate;
//     })
//     .filter((lead) =>
//       selectedLocation ? lead.location === selectedLocation : true
//     );
// }, [leads, filterStatus, startDate, endDate, selectedLocation]);


//   const totalPages = Math.ceil(filteredLeads.length / pageSize);

//   const paginatedLeads = filteredLeads.slice(
//     (currentPage - 1) * pageSize,
//     currentPage * pageSize
//   );

//   // Reset page when filters change
//   const resetPage = () => setCurrentPage(1);

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 flex flex-col h-full">
      
//       {/* === Filters Row === */}
//       <div className="flex items-center justify-end mb-4 gap-3 flex-wrap">

//         {/* Status Filter */}
//         {/* Status Filter */}
//         <select
//           value={filterStatus}
//           onChange={(e) => {
//             setFilterStatus(e.target.value);
//             resetPage();
//           }}
//           className="text-xs md:text-sm border border-slate-200 rounded-lg px-2 py-1"
//         >
//           <option value="All">All Statuses</option>
//           {STATUSES.map((s) => (
//             <option key={s.value} value={s.value}>
//               {s.label}
//             </option>
//           ))}
//         </select>


//         {/* Location Filter */}
//         <LocationFilter
//           locations={uniqueLocations}
//           selected={selectedLocation}
//           onChange={(loc) => {
//             setSelectedLocation(loc);
//             resetPage();
//           }}
//         />

//         {/* Date Range */}
//         <DateRangePicker
//           startDate={startDate}
//           endDate={endDate}
//           onChange={(start, end) => {
//             setStartDate(start);
//             setEndDate(end);
//             resetPage();
//           }}
//         />
//         <RefreshButton />
//       </div>

//       {/* === Table === */}
//       <div className="overflow-x-auto -mx-4 md:mx-0">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 pl-4">Lead</th>
//               <th className="px-2">Owner</th>
//               <th className="px-2">Location</th>
//               <th className="px-2">Status</th>
//               <th className="px-2">Value</th>
//               <th className="px-2">Date</th>
//             </tr>
//           </thead>

//           <tbody>
//             {paginatedLeads.length === 0 && (
//               <tr>
//                 <td colSpan="5" className="py-6 text-center text-slate-400">
//                   No leads match your filters.
//                 </td>
//               </tr>
//             )}

//             {paginatedLeads.map((lead) => {
//               const d = new Date(lead.createdAt);
//               const monthLabel = monthLabels[d.getMonth()] || "—";

//               const statusColor =
//                 lead.status === "Won"
//                   ? "bg-green-50 text-green-700 border-green-100"
//                   : lead.status === "Lost"
//                   ? "bg-red-50 text-red-700 border-red-100"
//                   : lead.status === "In Progress"
//                   ? "bg-amber-50 text-amber-700 border-amber-100"
//                   : "bg-blue-50 text-blue-700 border-blue-100";

//               return (
//                 <tr key={lead.id} className="border-b border-slate-50 last:border-0">
//                   <td className="py-2 pl-4">{lead.name}</td>
//                   <td className="px-2">{lead.owner}</td>
//                   <td className="px-2">{lead.location}</td>
//                   <td className="px-2">
//                     <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusColor}`}>
//                       {lead.status}
//                     </span>
//                   </td>
//                   <td className="px-2">{formatCurrency(lead.value)}</td>
//                   <td className="px-2">{monthLabel}</td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* === Pagination === */}
//       {totalPages > 1 && (
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={setCurrentPage}
//         />
//       )}
//     </div>
//   );
// }
